import { useCallback, useRef, useState } from 'react';

import { PAGES_BASE, updatePage } from './pages.api';
import { reorderSections, updateSection } from './sections.api';

import type { PageSection } from './pages.api';
import type { UpdateWebsitePageRequest } from '@utazzo/shared';

/**
 * Explicit-save model for the Custom Pages editor.
 *
 * Edits are queued locally into a dirty buffer (sections deduped by id, page
 * meta merged, order as the latest id list); nothing is persisted until the
 * caller invokes `save()` - wired to the header Save button. `dirty` drives the
 * button's enabled state and the unsaved-changes indicator/guard.
 */
export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface Dirty {
  sections: Map<string, PageSection>;
  meta: Partial<UpdateWebsitePageRequest> | null;
  order: string[] | null;
}

const emptyDirty = (): Dirty => ({ sections: new Map(), meta: null, order: null });

export function useEditorSave(pageId: string | null, basePath: string = PAGES_BASE) {
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [dirty, setDirty] = useState(false);
  const pending = useRef<Dirty>(emptyDirty());

  const markDirty = useCallback((): void => {
    setDirty(true);
    setSaveState('idle');
  }, []);

  const queueSection = useCallback(
    (section: PageSection): void => {
      pending.current.sections.set(section.id, section);
      markDirty();
    },
    [markDirty],
  );

  const queueMeta = useCallback(
    (payload: Partial<UpdateWebsitePageRequest>): void => {
      pending.current.meta = { ...pending.current.meta, ...payload };
      markDirty();
    },
    [markDirty],
  );

  const queueOrder = useCallback(
    (orderedIds: string[]): void => {
      pending.current.order = orderedIds;
      markDirty();
    },
    [markDirty],
  );

  const save = useCallback(async (): Promise<void> => {
    if (!pageId) return;
    const d = pending.current;
    const ops: Promise<unknown>[] = [];
    for (const [id, s] of d.sections) {
      ops.push(
        updateSection(id, {
          title: s.title,
          subtitle: s.subtitle,
          isVisible: s.isVisible,
          dataJson: s.data,
        }),
      );
    }
    if (d.meta) ops.push(updatePage(pageId, d.meta, basePath));
    if (d.order) ops.push(reorderSections(pageId, d.order));
    if (ops.length === 0) {
      setDirty(false);
      return;
    }
    setSaveState('saving');
    try {
      await Promise.all(ops);
      pending.current = emptyDirty();
      setDirty(false);
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  }, [pageId, basePath]);

  return { dirty, saveState, queueSection, queueMeta, queueOrder, save };
}
